import { useEffect, useState, useCallback } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Search, MoreHorizontal, Pencil, KeyRound, UserX, UserCheck, ShieldCheck, ChevronLeft, ChevronRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const emptyEdit = {
  id: null, full_name: "", email: "", mobile: "", pan: "", address: "",
  role: "customer", is_active: true, email_verified: false, mobile_verified: false,
};

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(15);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(emptyEdit);
  const [detail, setDetail] = useState(null); // { orders_count, paid_orders, lifetime_value }

  const [pwOpen, setPwOpen] = useState(false);
  const [pwUser, setPwUser] = useState(null);
  const [newPw, setNewPw] = useState("");

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmUser, setConfirmUser] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const params = { page, limit };
    if (q) params.q = q;
    if (role !== "all") params.role = role;
    if (status !== "all") params.status = status;
    try {
      const { data } = await api.get("/admin/users", { params });
      setUsers(data.users);
      setTotal(data.total);
    } catch (e) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [page, limit, q, role, status]);

  useEffect(() => { load(); }, [load]);

  const openEdit = async (u) => {
    try {
      const { data } = await api.get(`/admin/users/${u.id}`);
      setEditForm({ ...u });
      setDetail({
        orders_count: data.orders_count,
        paid_orders: data.paid_orders,
        lifetime_value: data.lifetime_value,
      });
      setEditOpen(true);
    } catch {
      toast.error("Could not fetch user");
    }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/admin/users/${editForm.id}`, {
        full_name: editForm.full_name,
        mobile: editForm.mobile,
        pan: editForm.pan,
        address: editForm.address,
        role: editForm.role,
        is_active: editForm.is_active,
        email_verified: editForm.email_verified,
        mobile_verified: editForm.mobile_verified,
      });
      toast.success("User updated");
      setEditOpen(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const doDeactivate = async () => {
    if (!confirmUser) return;
    try {
      if (confirmUser.is_active === false) {
        await api.post(`/admin/users/${confirmUser.id}/reactivate`);
        toast.success("User reactivated");
      } else {
        await api.delete(`/admin/users/${confirmUser.id}`);
        toast.success("User deactivated");
      }
      setConfirmOpen(false);
      setConfirmUser(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    }
  };

  const doResetPassword = async (e) => {
    e.preventDefault();
    if (!pwUser || newPw.length < 6) return toast.error("Password must be at least 6 characters");
    try {
      await api.post(`/admin/users/${pwUser.id}/reset-password`, { new_password: newPw });
      toast.success(`Password reset for ${pwUser.email}`);
      setPwOpen(false);
      setPwUser(null);
      setNewPw("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <DashboardLayout admin>
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="section-label">Admin</div>
          <h1 className="mt-1 font-display font-bold text-3xl text-[#0A192F]">Users</h1>
          <p className="text-slate-600 mt-1 text-sm">Manage customer and admin accounts. Deactivate, edit, and reset passwords.</p>
        </div>
        <div className="text-sm text-slate-500" data-testid="admin-users-count">{total} user{total === 1 ? "" : "s"}</div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <div className="relative md:col-span-2">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search email, name, mobile, PAN" value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} className="pl-9" data-testid="users-search" />
        </div>
        <Select value={role} onValueChange={(v) => { setPage(1); setRole(v); }}>
          <SelectTrigger data-testid="users-role-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            <SelectItem value="customer">Customers</SelectItem>
            <SelectItem value="admin">Admins</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={(v) => { setPage(1); setStatus(v); }}>
          <SelectTrigger data-testid="users-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="deactivated">Deactivated</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        {loading ? (
          <div className="p-6 text-slate-500 text-sm">Loading…</div>
        ) : users.length === 0 ? (
          <div className="p-10 text-center text-slate-500">No users match your filters.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">User</th>
                  <th className="text-left px-4 py-3">Mobile</th>
                  <th className="text-left px-4 py-3">Role</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Verified</th>
                  <th className="text-left px-4 py-3">Joined</th>
                  <th className="text-right px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map((u) => (
                  <tr key={u.id} className="hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-[#0A192F]">{u.full_name || "—"}</div>
                      <div className="text-xs text-slate-500 truncate">{u.email}</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{u.mobile ? `+91 ${u.mobile}` : "—"}</td>
                    <td className="px-4 py-3">
                      {u.role === "admin"
                        ? <span className="status-badge badge-info"><ShieldCheck className="w-3 h-3" /> Admin</span>
                        : <span className="status-badge badge-muted">Customer</span>}
                    </td>
                    <td className="px-4 py-3">
                      {u.is_active === false
                        ? <span className="status-badge badge-error">Deactivated</span>
                        : <span className="status-badge badge-success">Active</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600">
                      <div>{u.email_verified ? "✓ Email" : <span className="text-slate-400">Email</span>}</div>
                      <div>{u.mobile_verified ? "✓ Mobile" : <span className="text-slate-400">Mobile</span>}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`user-actions-${u.id}`}><MoreHorizontal className="w-4 h-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem onClick={() => openEdit(u)} data-testid={`user-edit-${u.id}`}>
                            <Pencil className="w-4 h-4 mr-2" /> Edit user
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { setPwUser(u); setNewPw(""); setPwOpen(true); }} data-testid={`user-pw-${u.id}`}>
                            <KeyRound className="w-4 h-4 mr-2" /> Reset password
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {me?.id !== u.id && (
                            <>
                              {u.is_active === false ? (
                                <DropdownMenuItem onClick={() => { setConfirmUser(u); setConfirmOpen(true); }} data-testid={`user-reactivate-${u.id}`}>
                                  <UserCheck className="w-4 h-4 mr-2 text-emerald-600" /> Reactivate
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem onClick={() => { setConfirmUser(u); setConfirmOpen(true); }} className="text-red-600" data-testid={`user-deactivate-${u.id}`}>
                                  <UserX className="w-4 h-4 mr-2" /> Deactivate
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                onClick={async () => {
                                  if (!window.confirm(`PERMANENTLY delete ${u.email}? This removes their orders, documents, and notifications.`)) return;
                                  try {
                                    await api.delete(`/admin/users/${u.id}/purge`);
                                    toast.success(`Deleted ${u.email}`);
                                    await load();
                                  } catch (err) {
                                    toast.error(err?.response?.data?.detail || "Delete failed");
                                  }
                                }}
                                className="text-red-700"
                                data-testid={`user-purge-${u.id}`}
                              >
                                <UserX className="w-4 h-4 mr-2" /> Delete permanently
                              </DropdownMenuItem>
                            </>
                          )}
                          {me?.id === u.id && (
                            <div className="px-2 py-1.5 text-xs text-slate-500">This is you</div>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="p-4 flex items-center justify-between border-t border-slate-200 text-sm">
          <div className="text-slate-500">Page {page} of {totalPages}</div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="users-prev-page"><ChevronLeft className="w-3.5 h-3.5" /></Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="users-next-page"><ChevronRight className="w-3.5 h-3.5" /></Button>
          </div>
        </div>
      </div>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit user</DialogTitle>
            <DialogDescription>{editForm.email}</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div className="p-3 rounded-md border border-slate-200 bg-slate-50">
                <div className="text-xs text-slate-500">Orders</div>
                <div className="font-semibold text-[#0A192F]">{detail.orders_count}</div>
              </div>
              <div className="p-3 rounded-md border border-slate-200 bg-slate-50">
                <div className="text-xs text-slate-500">Paid</div>
                <div className="font-semibold text-[#0A192F]">{detail.paid_orders}</div>
              </div>
              <div className="p-3 rounded-md border border-slate-200 bg-slate-50">
                <div className="text-xs text-slate-500">Lifetime value</div>
                <div className="font-semibold text-[#0A192F]">₹{detail.lifetime_value.toLocaleString("en-IN")}</div>
              </div>
            </div>
          )}
          <form onSubmit={saveEdit} className="space-y-4" data-testid="user-edit-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Full Name</Label>
                <Input required value={editForm.full_name} onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })} data-testid="user-edit-name" />
              </div>
              <div>
                <Label>Mobile</Label>
                <div className="flex">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-slate-50 text-slate-700 text-sm font-medium">+91</span>
                  <Input required value={editForm.mobile} onChange={(e) => setEditForm({ ...editForm, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) })} maxLength={10} inputMode="numeric" className="rounded-l-none" data-testid="user-edit-mobile" />
                </div>
              </div>
              <div>
                <Label>PAN</Label>
                <Input value={editForm.pan || ""} onChange={(e) => setEditForm({ ...editForm, pan: e.target.value.toUpperCase() })} className="font-mono uppercase" data-testid="user-edit-pan" />
              </div>
              <div>
                <Label>Role</Label>
                <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })} disabled={me?.id === editForm.id}>
                  <SelectTrigger data-testid="user-edit-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="customer">Customer</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
                {me?.id === editForm.id && (
                  <div className="text-xs text-slate-500 mt-1">You can't demote yourself.</div>
                )}
              </div>
            </div>
            <div>
              <Label>Address</Label>
              <Textarea rows={2} value={editForm.address || ""} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} data-testid="user-edit-address" />
            </div>
            <div className="grid grid-cols-3 gap-3 pt-2 border-t border-slate-100">
              <div className="flex items-center gap-2">
                <Switch checked={editForm.is_active !== false} onCheckedChange={(v) => setEditForm({ ...editForm, is_active: v })} disabled={me?.id === editForm.id} data-testid="user-edit-active" />
                <span className="text-sm">Active</span>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={!!editForm.email_verified} onCheckedChange={(v) => setEditForm({ ...editForm, email_verified: v })} data-testid="user-edit-email-verified" />
                <span className="text-sm">Email verified</span>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={!!editForm.mobile_verified} onCheckedChange={(v) => setEditForm({ ...editForm, mobile_verified: v })} data-testid="user-edit-mobile-verified" />
                <span className="text-sm">Mobile verified</span>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid="user-edit-save-btn">Save changes</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset password</DialogTitle>
            <DialogDescription>Set a new password for <span className="font-medium">{pwUser?.email}</span>. Share it securely with the user.</DialogDescription>
          </DialogHeader>
          <form onSubmit={doResetPassword} className="space-y-4" data-testid="user-pw-form">
            <div>
              <Label>New password</Label>
              <Input required type="text" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="Min 6 characters" data-testid="user-pw-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPwOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid="user-pw-save-btn">Set password</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirm deactivate/reactivate */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmUser?.is_active === false ? "Reactivate this user?" : "Deactivate this user?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmUser?.is_active === false
                ? <>The user <span className="font-medium">{confirmUser?.email}</span> will regain access to their account and existing orders.</>
                : <>The user <span className="font-medium">{confirmUser?.email}</span> will lose access. Their orders and documents are preserved and can be restored by reactivating.</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="user-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDeactivate}
              className={confirmUser?.is_active === false ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}
              data-testid="user-confirm-action"
            >
              {confirmUser?.is_active === false ? "Yes, reactivate" : "Yes, deactivate"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
